import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import HelpIcon from "@mui/icons-material/HelpOutlineRounded";
import { Box, Button, Card, CardContent, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router";
import CopyToBufferButton from "@entrilia/shared/components/inputs/CopyToBufferButton";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { InboxChannel } from "../../../../api/types/inboxesTypes";
import AgentAvatar from "./AgentAvatar";
import AgentInboxChannelStatusChip from "./AgentInboxChannelStatusChip";
import AgentInboxChannelTypeAvatar from "./AgentInboxChannelTypeAvatar";
import AgentInboxSettingsContext from "./AgentInboxSettingsContext";
import DeleteInboxChannelDialog from "./DeleteInboxChannelDialog";
import { startEditingChannelAction } from "./editInboxChannelState";

interface Props {
  channel: InboxChannel;
}

const AgentInboxChannelCard = ({ channel }: Props) => {
  const { hasEditPermissions, setEditedChannelState, onInboxUpdated, agents } =
    useDefinedContext(AgentInboxSettingsContext);

  const [, setSearchParams] = useSearchParams();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleEdit = () => {
    setEditedChannelState(startEditingChannelAction(channel));
    setSearchParams({ channel: channel.id });
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <AgentInboxChannelTypeAvatar channelType={channel.type} size={40} />

              <Stack spacing={0.5}>
                <Stack spacing={1} direction="row" sx={{ alignItems: "center" }}>
                  <Typography variant="subtitle1">{channel.name}</Typography>
                  <AgentInboxChannelStatusChip isActive={channel.isActive} />
                </Stack>

                <Stack spacing={1} direction="row" sx={{ alignItems: "center" }}>
                  {channel.dedicatedWorkflowAgentTypes.map((agentType) => {
                    const agent = agents.find((a) => a.workflowAgentType === agentType);
                    return (
                      <Stack key={agentType} spacing={1} direction="row" sx={{ alignItems: "center" }}>
                        <AgentAvatar size={24} />
                        <Typography>{agent?.agentName || agentType}</Typography>
                        {agent?.agentDescription && (
                          <Tooltip arrow title={agent.agentDescription}>
                            <HelpIcon sx={{ color: "action.active" }} />
                          </Tooltip>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>

                {channel.address && (
                  <Stack spacing={1} direction="row" sx={{ alignItems: "center" }}>
                    <Typography>{channel.address}</Typography>
                    <CopyToBufferButton textToCopy={channel.address} />
                  </Stack>
                )}
              </Stack>
            </Stack>

            {hasEditPermissions && (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Button variant="outlined" color="secondary" startIcon={<EditIcon />} onClick={handleEdit}>
                  Edit
                </Button>
                <IconButton color="error" onClick={() => setIsDeleteDialogOpen(true)}>
                  <DeleteIcon />
                </IconButton>
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>
      {isDeleteDialogOpen && (
        <DeleteInboxChannelDialog
          channelId={channel.id}
          onClose={() => setIsDeleteDialogOpen(false)}
          onDeleted={onInboxUpdated}
        />
      )}
    </>
  );
};

export default AgentInboxChannelCard;
